/**
 * Affiliate API helpers.
 * O token do afiliado é guardado em `kinng.affiliate.token` (separado do token
 * do tenant/admin em `kinng.token`) para permitir sessões concorrentes.
 */

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";

const AFF_TOKEN_KEY = "kinng.affiliate.token";

export const affiliateAuth = {
    getToken: () => { try { return localStorage.getItem(AFF_TOKEN_KEY); } catch { return null; } },
    setToken: (t) => { try { localStorage.setItem(AFF_TOKEN_KEY, t); } catch { /* ignore */ } },
    clear: () => { try { localStorage.removeItem(AFF_TOKEN_KEY); } catch { /* ignore */ } },
};

const req = (path, opts = {}, useAffToken = false) => {
    const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
    const token = useAffToken ? affiliateAuth.getToken() : (localStorage.getItem("kinng.token") || null);
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${API_BASE}${path}`, { ...opts, headers }).then(async (r) => {
        let data = null;
        try { data = await r.json(); } catch { /* ignore */ }
        if (!r.ok) throw new Error(data?.detail || `Erro ${r.status}`);
        return data;
    });
};

// ─── Admin (usa kinng.token) ────────────────────────────────────────────
export const affiliateAdminApi = {
    listAffiliates: (statusFilter) =>
        req(`/admin/affiliates${statusFilter && statusFilter !== "all" ? `?status_filter=${statusFilter}` : ""}`),
    createAffiliate: (body) => req(`/admin/affiliates`, { method: "POST", body: JSON.stringify(body) }),
    updateAffiliate: (id, body) => req(`/admin/affiliates/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    approveAffiliate: (id) => req(`/admin/affiliates/${id}/approve`, { method: "PATCH" }),
    suspendAffiliate: (id) => req(`/admin/affiliates/${id}/suspend`, { method: "PATCH" }),
    affiliateCommissions: (id) => req(`/admin/affiliates/${id}/commissions`),
    listCommissions: (filter) =>
        req(`/admin/commissions${filter && filter !== "all" ? `?status_filter=${filter}` : ""}`),
    payCommission: (id, body) => req(`/admin/commissions/${id}/pay`, { method: "PATCH", body: JSON.stringify(body) }),
    getSettings: () => req(`/admin/affiliate-settings`),
    updateSettings: (body) => req(`/admin/affiliate-settings`, { method: "PATCH", body: JSON.stringify(body) }),
    settingsHistory: () => req(`/admin/affiliate-settings/history`),
    performance: () => req(`/admin/affiliates/performance`),
    registerTenantPayment: (tenantId, body) =>
        req(`/admin/tenants/${tenantId}/register-payment`, { method: "POST", body: JSON.stringify(body) }),
};

// ─── Público ────────────────────────────────────────────────────────────
export const affiliatePublicApi = {
    register: (body) => req(`/public/affiliate/register`, { method: "POST", body: JSON.stringify(body) }),
    // Contrato do Programa de Afiliados — conteúdo + versão vigentes (sem auth).
    contract: () => req(`/public/affiliate-contract`),
};

// ─── Portal (usa kinng.affiliate.token) ────────────────────────────────
export const affiliatePortalApi = {
    login: async (email, password) => {
        const r = await req(`/affiliate/auth/login`, {
            method: "POST", body: JSON.stringify({ email, password }),
        });
        affiliateAuth.setToken(r.token);
        return r;
    },
    logout: () => affiliateAuth.clear(),
    me: () => req(`/affiliate/me`, {}, true),
    dashboard: () => req(`/affiliate/dashboard`, {}, true),
    referrals: () => req(`/affiliate/referrals`, {}, true),
    commissions: () => req(`/affiliate/commissions`, {}, true),
    updateRefCode: (refCode) => req(`/affiliate/ref-code`, { method: "PATCH", body: JSON.stringify({ ref_code: refCode }) }, true),
    // Contrato de Afiliados — leitura pública + aceite da versão vigente (gate do portal).
    contract: () => req(`/public/affiliate-contract`),
    acceptContract: () => req(`/affiliate/contract/accept`, { method: "POST" }, true),
    pdfUrl: () => `${API_BASE}/affiliate/commissions/pdf`,
    pdfBlob: async () => {
        const token = affiliateAuth.getToken();
        const res = await fetch(`${API_BASE}/affiliate/commissions/pdf`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Erro ${res.status}`);
        return await res.blob();
    },
};

export default { affiliateAdminApi, affiliatePublicApi, affiliatePortalApi, affiliateAuth };
